import {ReactElement, useEffect, useRef, useState} from "react";
import {
    OriginalRevenueRenewalIncreaseType,
    useValidateRenewalIncreaseFormulaLazyQuery
} from "../../../../__generated__/generated_types";
import {Dropdown, Field, Item, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input} from "@zendeskgarden/react-forms";
import {FormulaField, ValidateResult} from "./components/FormulaField";
import {Button} from "@zendeskgarden/react-buttons";
import * as css from "./renewalIncrease.module.scss";
import {renewalIncreaseTypePrettyName} from "./renewal-increase-helpers";
import {toast} from "react-toastify";

export interface RenewalIncreaseConfigurationProps {
    disabled?: boolean,
    type: OriginalRevenueRenewalIncreaseType | null,
    percent: number | null,
    customFormula: string | null,
    onSave: (type: OriginalRevenueRenewalIncreaseType | null, percent: number | null, customFormula: string | null) => void,
}

export function RenewalIncreaseConfiguration(props: RenewalIncreaseConfigurationProps): ReactElement {
    const [type, setType] = useState<OriginalRevenueRenewalIncreaseType | null>(props.type);
    const [percent, setPercent] = useState<number | null>(props.percent);
    const [customFormula, setCustomFormula] = useState<string | null>(props.customFormula);
    const [pendingSave, setPendingSave] = useState<string | null>(null);
    const [validateFormula, {data: validationData, loading: validationLoading}] = useValidateRenewalIncreaseFormulaLazyQuery({
        fetchPolicy: "no-cache"
    });
    // Yes, you want this to be a separate instance of the same graphql call. It's the only way to properly keep the reactivity
    // from causing issues when the callback is used instead of a check on save.
    const [checkFormula, {data: checkData, error: checkError, loading: checkLoading}] = useValidateRenewalIncreaseFormulaLazyQuery({
        fetchPolicy: "no-cache"
    });
    const checkResolve = useRef<((val: ValidateResult) => void) | undefined>(undefined);
    const checkReject = useRef<((reason?: any) => void) | undefined>(undefined);

    function doCheck(formula: string): Promise<ValidateResult> {
        checkFormula({
            variables: {
                formula
            }
        });
        return new Promise((res, rej) => {
            checkResolve.current = res;
            checkReject.current = rej;
        });
    }

    useEffect(() => {
        if(checkLoading || !checkResolve.current || !checkReject.current) {
            return;
        }

        if(checkError) {
            checkReject.current(checkError.message);
        } else if(!checkData) {
            checkReject.current("No data");
        } else {
            checkResolve.current(checkData.validateRenewalIncreaseFormula);
        }
    }, [checkData, checkLoading]);

    useEffect(() => {
        if (validationData == null || pendingSave == null) {
            return;
        }

        if (validationData.validateRenewalIncreaseFormula.valid) {
            props.onSave(OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease, null, pendingSave);
        } else {
            toast.error(validationData.validateRenewalIncreaseFormula.error ?? "Invalid formula");
        }
        setPendingSave(null);
    }, [validationData]);

    function handleSelectType(newVal: OriginalRevenueRenewalIncreaseType | "manual-entry") {
        if (newVal === "manual-entry") {
            setType(null);
        }
        else {
            setType(newVal);
        }
        setPercent(null);
        setCustomFormula(null);
    }

    function handleChangePercent(evt: any) {
        const sanitized = evt.target.value === undefined || evt.target.value == null || evt.target.value === "" ? null : +evt.target.value;
        if (!Number.isNaN(sanitized)) {
            setPercent(sanitized);
        }
        else {
            setPercent(null);
        }
    }

    function handleChangeCustomFormula(evt: any) {
        const value = evt.target.value === undefined || evt.target.value == null || evt.target.value === "" ? null : evt.target.value;
        setCustomFormula(value);
    }

    function handleSave() {
        let percentSave = null;
        let customFormulaSave = null;
        if (type !== null) {
            if (type === OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease) {
                customFormulaSave = customFormula;

                if (customFormulaSave) {
                    setPendingSave(customFormulaSave);
                    validateFormula({variables: {formula: customFormulaSave}});
                    return;
                }
            } else {
                percentSave = percent;
            }
        }
        props.onSave(type, percentSave, customFormulaSave);
    }

    function handleCancel() {
        setType(props.type);
        setPercent(props.percent);
        setCustomFormula(props.customFormula);
    }

    return (
        <div className={css.wrapper}>
            <Dropdown
                selectedItem={type}
                onSelect={handleSelectType}
            >
                <Field className={css.dropdown}>
                    <Select disabled={props.disabled}>
                        {renewalIncreaseTypePrettyName(type ?? "manual-entry")}
                    </Select>
                </Field>
                <Menu>
                    <Item key={OriginalRevenueRenewalIncreaseType.MarketOrInPlaceRent} value={OriginalRevenueRenewalIncreaseType.MarketOrInPlaceRent}>
                        {renewalIncreaseTypePrettyName(OriginalRevenueRenewalIncreaseType.MarketOrInPlaceRent)}
                    </Item>
                    <Item key={OriginalRevenueRenewalIncreaseType.MarketRentDiscount} value={OriginalRevenueRenewalIncreaseType.MarketRentDiscount}>
                        {renewalIncreaseTypePrettyName(OriginalRevenueRenewalIncreaseType.MarketRentDiscount)}
                    </Item>
                    <Item key={OriginalRevenueRenewalIncreaseType.MarkToMarketOrInPlaceRent} value={OriginalRevenueRenewalIncreaseType.MarkToMarketOrInPlaceRent}>
                        {renewalIncreaseTypePrettyName(OriginalRevenueRenewalIncreaseType.MarkToMarketOrInPlaceRent)}
                    </Item>
                    <Item key={OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease} value={OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease}>
                        {renewalIncreaseTypePrettyName(OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease)}
                    </Item>
                    <Item key={"key-manual"} value={"manual-entry"}>
                        {renewalIncreaseTypePrettyName("manual-entry")}
                    </Item>
                </Menu>
            </Dropdown>
            {type !== null && type !== OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease &&
            <FormField className={css.input}>
                <Input
                    disabled={props.disabled}
                    value={percent ?? ""}
                    type="number"
                    onChange={handleChangePercent}
                    placeholder={"Type % amount"}
                />
            </FormField>
            }
            {type === OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease &&
                <FormulaField
                    disabled={props.disabled}
                    value={customFormula ?? ""}
                    onChange={handleChangeCustomFormula}
                    className={css.input}
                    validateFormula={doCheck}
                />
            }
            {(type !== props.type || percent !== props.percent || customFormula !== props.customFormula) &&
            <div>
                <Button
                    isPrimary
                    disabled={validationLoading ||
                             (type === props.type && percent === props.percent && customFormula === props.customFormula) ||
                             (type !== null && type !== OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease && percent == null) ||
                             (type === OriginalRevenueRenewalIncreaseType.CustomRenewalIncrease && (customFormula == null || customFormula.trim() === ""))}
                    onClick={handleSave}
                >
                    Save
                </Button>
                <Button
                    disabled={type === props.type && percent === props.percent && customFormula === props.customFormula}
                    onClick={handleCancel}
                    style={{marginLeft: "0.5rem"}}
                >
                    Cancel
                </Button>
            </div>
            }
        </div>
    );
}
